import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, AlertCircle, DollarSign, Building2, Plus, Trash2, Star } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { useCurrency } from '../context/CurrencyContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const WalletPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getUserWalletTransactions } = useApp();
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newBankDetails, setNewBankDetails] = useState({
    bankName: '',
    bsb: '',
    accountNumber: '',
    accountName: '',
    paypalEmail: '',
    payoutMethod: 'bank',
    saveAccount: true
  });

  // Fetch saved bank accounts
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchBankAccounts();
      fetchPayoutRequests();
    }
  }, [isAuthenticated, user?.id]);

  const fetchBankAccounts = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/users/${user.id}/bank-accounts`);
      if (response.ok) {
        const accounts = await response.json();
        setSavedAccounts(accounts);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const fetchPayoutRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/wallet/${user.id}/payout-requests`);
      if (response.ok) {
        const data = await response.json();
        setPayoutRequests(data);
      }
    } catch (error) {
      console.error('Error fetching payout requests:', error);
    }
  };

  const handleAddBankAccount = async () => {
    if (newBankDetails.payoutMethod === 'bank') {
      if (!newBankDetails.bankName || !newBankDetails.bsb || !newBankDetails.accountNumber || !newBankDetails.accountName) {
        toast.error('Please fill in all bank details');
        return;
      }
    } else {
      if (!newBankDetails.paypalEmail) {
        toast.error('Please enter your PayPal email');
        return;
      }
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/users/${user.id}/bank-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bank_name: newBankDetails.bankName,
          bsb: newBankDetails.bsb,
          account_number: newBankDetails.accountNumber,
          account_name: newBankDetails.accountName,
          paypal_email: newBankDetails.paypalEmail,
          is_default: savedAccounts.length === 0
        })
      });

      if (response.ok) {
        toast.success('Payout details saved!');
        setShowAddBankModal(false);
        setNewBankDetails({ bankName: '', bsb: '', accountNumber: '', accountName: '', paypalEmail: '', payoutMethod: 'bank', saveAccount: true });
        fetchBankAccounts();
      } else {
        toast.error('Failed to save payout details');
      }
    } catch (error) {
      toast.error('Error saving payout details');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!confirm('Are you sure you want to delete this bank account?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/users/${user.id}/bank-accounts/${accountId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Bank account deleted');
        fetchBankAccounts();
      }
    } catch (error) {
      toast.error('Error deleting account');
    }
  };

  const handleSetDefault = async (accountId) => {
    try {
      const response = await fetch(`${API_BASE}/api/users/${user.id}/bank-accounts/${accountId}/default`, {
        method: 'PUT'
      });

      if (response.ok) {
        toast.success('Default account updated');
        fetchBankAccounts();
      }
    } catch (error) {
      toast.error('Error updating default');
    }
  };

  if (!isAuthenticated) {
    return (
      <Layout title="Wallet">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <Wallet className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to view wallet</h2>
          <p className="text-gray-500 text-center mb-4">Track your earnings and withdraw funds</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  const transactions = getUserWalletTransactions();
  const { currency, toViewer } = useCurrency();

  // Wallet is held in AUD; show the viewer's currency big with the AUD figure underneath
  const MONEY_SYMBOLS = { AUD: '$', GBP: '£', USD: 'US$', EUR: '€', NZD: 'NZ$', CAD: 'CA$' };
  const fmtMoney = (aud) => {
    const amount = Number(aud) || 0;
    if (currency === 'AUD') return { primary: `$${amount.toFixed(2)}`, secondary: null };
    const converted = toViewer(amount, 'AUD');
    if (converted === null) return { primary: `A$${amount.toFixed(2)}`, secondary: null };
    return {
      primary: `${MONEY_SYMBOLS[currency] || ''}${converted.toFixed(2)}`,
      secondary: `A$${amount.toFixed(2)}`
    };
  };
  
  const wallet = {
    availableBalance: user?.wallet?.availableBalance ?? 0,
    pendingBalance: user?.wallet?.pendingBalance ?? 0,
    totalEarnings: user?.wallet?.totalEarnings ?? 0
  };

  const handleRequestPayout = async () => {
    const amount = payoutAmount ? parseFloat(payoutAmount) : wallet.availableBalance;
    
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amount > wallet.availableBalance) {
      toast.error(`Amount exceeds available balance of A$${wallet.availableBalance.toFixed(2)}`);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/wallet/${user.id}/request-payout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Payout of A$${amount.toFixed(2)} requested! Admin will transfer via Stripe.`);
        setShowPayoutModal(false);
        setPayoutAmount('');
        fetchPayoutRequests();
        window.location.reload();
      } else {
        toast.error(result.detail || 'Request failed');
      }
    } catch (error) {
      toast.error('Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <Badge className="bg-minted-100 text-minted-700">Available</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-700">Processing</Badge>;
      case 'completed':
        return <Badge className="bg-gray-100 text-gray-700">Completed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Layout title="Wallet" showBack>
      {/* Balance Cards */}
      <div className="bg-minted-900 px-4 py-6 text-white">
        <p className="text-[10px] tracking-[0.25em] uppercase text-stone-400 mb-1">Your Wallet</p>
        <div className="text-center mb-6">
          <p className="text-minted-300 text-xs tracking-wider uppercase mb-1">Available Balance</p>
          <p className="text-4xl font-display" data-testid="wallet-available-balance">{fmtMoney(wallet.availableBalance).primary}</p>
          {fmtMoney(wallet.availableBalance).secondary && (
            <p className="text-xs text-minted-400 mt-1" data-testid="wallet-aud-note">≈ {fmtMoney(wallet.availableBalance).secondary}</p>
          )}
        </div>
        
        <div className="flex gap-3">
          <div className="flex-1 bg-white/10 rounded-sm p-3 text-center">
            <Clock className="w-5 h-5 mx-auto mb-1 text-minted-300" />
            <p className="text-lg font-display">{fmtMoney(wallet.pendingBalance).primary}</p>
            <p className="text-[10px] text-minted-400 tracking-wider uppercase">Pending</p>
          </div>
          <div className="flex-1 bg-white/10 rounded-sm p-3 text-center">
            <DollarSign className="w-5 h-5 mx-auto mb-1 text-minted-300" />
            <p className="text-lg font-display">{fmtMoney(wallet.totalEarnings).primary}</p>
            <p className="text-[10px] text-minted-400 tracking-wider uppercase">Total Earnings</p>
          </div>
        </div>

        <Button
          onClick={() => {
            if (savedAccounts.length === 0) {
              toast.error('Please add your bank details first');
              setShowAddBankModal(true);
              return;
            }
            setPayoutAmount(wallet.availableBalance.toFixed(2));
            setShowPayoutModal(true);
          }}
          className="w-full mt-4 bg-white text-minted-900 hover:bg-stone-50 rounded-sm"
          disabled={wallet.availableBalance <= 0}
          data-testid="request-payout-btn"
        >
          <ArrowUpRight className="w-4 h-4 mr-2" />
          Request Payout
        </Button>
      </div>

      {/* Saved Payout Accounts */}
      <div className="px-4 py-4 border-b">
        {/* Prompt banner if seller has earnings but no payout details */}
        {savedAccounts.length === 0 && (wallet.pendingBalance > 0 || wallet.availableBalance > 0) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3" data-testid="payout-details-prompt">
            <p className="text-sm font-medium text-amber-800">Add your payout details</p>
            <p className="text-xs text-amber-600 mt-0.5">You have funds waiting! Add a bank account or PayPal to withdraw your earnings.</p>
            <Button 
              size="sm" 
              className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs"
              onClick={() => setShowAddBankModal(true)}
            >
              Add Payout Details
            </Button>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Payout Accounts</h3>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowAddBankModal(true)}
            className="text-minted-600 border-minted-300"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>

        {savedAccounts.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No bank accounts saved</p>
            <Button 
              variant="link" 
              className="text-minted-600 text-sm"
              onClick={() => setShowAddBankModal(true)}
            >
              Add your first bank account
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {savedAccounts.map((account) => (
              <div 
                key={account.id} 
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  account.is_default ? 'border-minted-300 bg-minted-50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm flex items-center gap-2">
                      {account.paypal_email ? 'PayPal' : account.bank_name}
                      {account.is_default && (
                        <Badge className="bg-minted-100 text-minted-700 text-xs">Default</Badge>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {account.paypal_email 
                        ? account.paypal_email 
                        : `BSB: ${account.bsb} • Acc: ${account.account_number_masked || '****' + account.account_number?.slice(-4)}`
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!account.is_default && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleSetDefault(account.id)}
                      className="text-gray-500 hover:text-minted-600"
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeleteAccount(account.id)}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 px-4 py-3 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-900 font-medium">How payments work</p>
          <p className="text-xs text-blue-700">Funds become available when the buyer confirms receipt or 7 days after delivery.</p>
        </div>
      </div>

      {/* Transactions */}
      <div className="px-4 py-4">
        <h3 className="font-semibold text-gray-900 mb-3">Transaction History</h3>
        
        {transactions.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Wallet className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${
                      transaction.amount > 0 ? 'bg-minted-100' : 'bg-red-100'
                    }`}>
                      {transaction.amount > 0 ? (
                        <ArrowDownLeft className="w-4 h-4 text-minted-600" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{transaction.description}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${
                      transaction.amount > 0 ? 'text-minted-600' : 'text-red-600'
                    }`}>
                      {transaction.amount > 0 ? '+' : ''}${Math.abs(transaction.amount).toFixed(2)}
                    </p>
                    {getStatusBadge(transaction.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payout Requests */}
      {payoutRequests.length > 0 && (
        <div className="px-4 py-4 border-b">
          <h3 className="font-semibold text-gray-900 mb-3">Payout Requests</h3>
          <div className="space-y-2">
            {payoutRequests.map((req) => (
              <div key={req.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white">
                <div>
                  <p className="font-medium text-sm text-gray-900">${req.amount?.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{new Date(req.created_at).toLocaleDateString()}</p>
                </div>
                <Badge className={
                  req.status === 'completed' ? 'bg-green-100 text-green-700' :
                  req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                } data-testid={`payout-status-${req.id}`}>
                  {req.status === 'completed' ? 'Paid' : req.status === 'rejected' ? 'Declined' : 'Pending'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Payout</DialogTitle>
            <DialogDescription>
              Available: ${wallet.availableBalance.toFixed(2)} — Admin will transfer via Stripe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (A$ — Australian dollars)</Label>
              <Input
                type="number"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="0.00"
                max={wallet.availableBalance}
                className="mt-1"
                data-testid="payout-amount-input"
              />
              <Button
                variant="link"
                className="text-minted-600 text-xs p-0 mt-1"
                onClick={() => setPayoutAmount(wallet.availableBalance.toFixed(2))}
              >
                Request full balance
              </Button>
            </div>
            {savedAccounts.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Payout will be sent to:</p>
                <p className="text-sm font-medium text-gray-900">
                  {savedAccounts[0]?.paypal_email 
                    ? `PayPal: ${savedAccounts[0].paypal_email}` 
                    : `${savedAccounts[0]?.bank_name} — BSB: ${savedAccounts[0]?.bsb} • ****${savedAccounts[0]?.account_number?.slice(-4)}`
                  }
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-minted-600 hover:bg-minted-700" 
              onClick={handleRequestPayout}
              disabled={loading}
              data-testid="confirm-payout-btn"
            >
              {loading ? 'Requesting...' : `Request $${payoutAmount || '0.00'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payout Details Modal */}
      <Dialog open={showAddBankModal} onOpenChange={setShowAddBankModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payout Details</DialogTitle>
            <DialogDescription>
              Save your payment details for withdrawals
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Payout Method Toggle */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setNewBankDetails({ ...newBankDetails, payoutMethod: 'bank' })}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  newBankDetails.payoutMethod === 'bank' 
                    ? 'bg-white shadow-sm text-minted-700' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="payout-method-bank"
              >
                <Building2 className="w-4 h-4 inline mr-1.5" />
                Bank Transfer
              </button>
              <button
                onClick={() => setNewBankDetails({ ...newBankDetails, payoutMethod: 'paypal' })}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  newBankDetails.payoutMethod === 'paypal' 
                    ? 'bg-white shadow-sm text-blue-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="payout-method-paypal"
              >
                PayPal
              </button>
            </div>

            {newBankDetails.payoutMethod === 'bank' ? (
              <>
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    value={newBankDetails.bankName}
                    onChange={(e) => setNewBankDetails({ ...newBankDetails, bankName: e.target.value })}
                    placeholder="e.g. Commonwealth Bank"
                    className="mt-1"
                    data-testid="bank-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>BSB</Label>
                    <Input
                      value={newBankDetails.bsb}
                      onChange={(e) => setNewBankDetails({ ...newBankDetails, bsb: e.target.value })}
                      placeholder="000-000"
                      className="mt-1"
                      data-testid="bsb-input"
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={newBankDetails.accountNumber}
                      onChange={(e) => setNewBankDetails({ ...newBankDetails, accountNumber: e.target.value })}
                      placeholder="12345678"
                      className="mt-1"
                      data-testid="account-number-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Account Name</Label>
                  <Input
                    value={newBankDetails.accountName}
                    onChange={(e) => setNewBankDetails({ ...newBankDetails, accountName: e.target.value })}
                    placeholder="Your full name as on account"
                    className="mt-1"
                    data-testid="account-name-input"
                  />
                </div>
              </>
            ) : (
              <div>
                <Label>PayPal Email</Label>
                <Input
                  type="email"
                  value={newBankDetails.paypalEmail}
                  onChange={(e) => setNewBankDetails({ ...newBankDetails, paypalEmail: e.target.value })}
                  placeholder="your@paypal.com"
                  className="mt-1"
                  data-testid="paypal-email-input"
                />
                <p className="text-xs text-gray-400 mt-1">The email linked to your PayPal account</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBankModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-minted-600 hover:bg-minted-700" 
              onClick={handleAddBankAccount}
              disabled={loading}
              data-testid="save-payout-details-btn"
            >
              {loading ? 'Saving...' : 'Save Details'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default WalletPage;
